/**
 * CustomQuickAddDialog — the "Quick Add Custom Item" modal previously
 * lived inline inside ProductsPage.js (~236 LOC). It handles the
 * lowest-friction path for staff to add an off-catalog product to the
 * quotation with support for custom rate types, sq-ft math, product
 * codes and quantity steppers.
 *
 * Extracted from ProductsPage.js. This dialog is a
 * controlled component — the parent owns `data`, opens/closes the
 * dialog, and provides `onSubmit` which handles cart insertion.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Zap, Plus } from 'lucide-react';
import { formatINR } from '../../../lib/store';
import { RATE_TYPE_OPTIONS, SIZE_UNIT_OPTIONS } from '../constants';
import { calculateQuickAddSqFt } from '../customQuickAddSqFt';

export { calculateQuickAddSqFt };

export function CustomQuickAddDialog({
  open,
  onOpenChange,
  data,
  setData,
  categories,
  onSubmit,
}) {
  const sqFt = calculateQuickAddSqFt(data);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-heda-line bg-white sm:rounded-3xl max-w-md max-h-[85vh] overflow-y-auto"
        data-testid="custom-quick-add-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-heda-ink flex items-center gap-2">
            <Zap className="w-5 h-5 text-heda-gold" /> Quick Add Item
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-heda-sub">Add any product that&apos;s not in the list.</p>

          {/* Category Selection */}
          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Product Category *</Label>
            <Select
              value={data.category}
              onValueChange={(val) => setData(d => ({ ...d, category: val }))}
            >
              <SelectTrigger className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line" data-testid="quick-add-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
                <SelectItem value="Others">Custom / Other</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-heda-mute mt-1">Product will appear under this category in PDF</p>
          </div>

          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Product Name *</Label>
            <Input
              value={data.name}
              onChange={(e) => setData(d => ({ ...d, name: e.target.value }))}
              placeholder="e.g., Abru Tape, Hinges..."
              className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line"
              data-testid="custom-name-input"
            />
          </div>

          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Product Code (Optional)</Label>
            <Input
              value={data.product_code}
              onChange={(e) => setData(d => ({ ...d, product_code: e.target.value }))}
              placeholder="e.g., LM-1234, HW-567..."
              className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line"
              data-testid="custom-code-input"
            />
            <p className="text-[10px] text-heda-mute mt-1">If provided, only code will show in customer PDF</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Rate *</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={data.rate}
                onChange={(e) => setData(d => ({ ...d, rate: e.target.value }))}
                placeholder="e.g., 1500"
                className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line"
                data-testid="custom-rate-input"
              />
            </div>

            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Rate Type *</Label>
              <Select
                value={data.rate_type}
                onValueChange={(val) => setData(d => ({ ...d, rate_type: val }))}
              >
                <SelectTrigger className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line" data-testid="custom-rate-type-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {RATE_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Custom Rate Type Input */}
          {data.rate_type === 'custom' && (
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Custom Rate Type *</Label>
              <Input
                value={data.custom_rate_type}
                onChange={(e) => setData(d => ({ ...d, custom_rate_type: e.target.value }))}
                placeholder="e.g., per bundle, per roll..."
                className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line"
                data-testid="custom-rate-type-input"
              />
            </div>
          )}

          {/* Size input - only show when rate type is per sq ft */}
          {data.rate_type === 'per sq ft' && (
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Size *</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  value={data.sizeLength}
                  onChange={(e) => setData(d => ({ ...d, sizeLength: e.target.value }))}
                  placeholder="Length"
                  className="rounded-xl w-20 bg-heda-cream/50 border-heda-line text-center"
                  data-testid="custom-size-length"
                />
                <span className="text-lg font-bold text-heda-sub">×</span>
                <Input
                  type="number"
                  value={data.sizeWidth}
                  onChange={(e) => setData(d => ({ ...d, sizeWidth: e.target.value }))}
                  placeholder="Width"
                  className="rounded-xl w-20 bg-heda-cream/50 border-heda-line text-center"
                  data-testid="custom-size-width"
                />
                <Select
                  value={data.sizeUnit}
                  onValueChange={(val) => setData(d => ({ ...d, sizeUnit: val }))}
                >
                  <SelectTrigger className="rounded-xl w-24 bg-heda-cream/50 border-heda-line" data-testid="custom-size-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIZE_UNIT_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-heda-mute mt-1">
                = {sqFt.toFixed(2)} sq ft
              </p>
            </div>
          )}

          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Specs / Notes (Optional)</Label>
            <Textarea
              value={data.specs}
              onChange={(e) => setData(d => ({ ...d, specs: e.target.value }))}
              placeholder="e.g., 8x4 size, Marine grade..."
              className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line h-12 resize-none"
              data-testid="custom-specs-input"
            />
          </div>

          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Quantity</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newQty = Math.max(1, (parseInt(data.quantityInput) || 1) - 1);
                  setData(d => ({ ...d, quantity: newQty, quantityInput: String(newQty) }));
                }}
                className="rounded-xl h-10 w-10 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
              >-</Button>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={data.quantityInput}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setData(d => ({
                    ...d,
                    quantityInput: val,
                    quantity: val ? parseInt(val) || 1 : 1,
                  }));
                }}
                onBlur={() => {
                  if (!data.quantityInput || parseInt(data.quantityInput) < 1) {
                    setData(d => ({ ...d, quantityInput: '1', quantity: 1 }));
                  }
                }}
                className="rounded-xl bg-heda-cream/50 text-center h-10 w-20 font-bold text-lg"
                data-testid="custom-quantity-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newQty = (parseInt(data.quantityInput) || 1) + 1;
                  setData(d => ({ ...d, quantity: newQty, quantityInput: String(newQty) }));
                }}
                className="rounded-xl h-10 w-10 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
              >+</Button>
            </div>
          </div>

          {/* Total Preview with calculation breakdown */}
          {data.rate && (
            <div className="p-2 bg-heda-gold-tint rounded-xl border border-heda-gold/30">
              <div className="flex items-center justify-between">
                <p className="text-xs text-heda-gold uppercase tracking-wide">Total</p>
                {data.rate_type === 'per sq ft' ? (
                  <div className="text-right">
                    <p className="text-[10px] text-heda-sub">
                      ₹{parseFloat(data.rate).toFixed(2)}/sq ft × {sqFt.toFixed(2)} sq ft × {data.quantity}
                    </p>
                    <p className="text-lg font-bold font-mono-data text-heda-ink">
                      {formatINR((parseFloat(data.rate) || 0) * sqFt * data.quantity)}
                    </p>
                  </div>
                ) : (
                  <p className="text-lg font-bold font-mono-data text-heda-ink">
                    {formatINR((parseFloat(data.rate) || 0) * data.quantity)}
                  </p>
                )}
              </div>
            </div>
          )}

          <Button
            onClick={onSubmit}
            className="rounded-xl w-full bg-heda-gold hover:bg-heda-gold/90 text-white uppercase tracking-wider font-semibold h-10"
            data-testid="confirm-custom-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add to Quotation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
